import React, { useState, useEffect, useRef } from 'react';
import { Card, CardBody, Col, Row, Badge, Button, Input, Label, Spinner } from 'reactstrap';
import { Download, Truck, MapPin, Printer, FileText, Search, Calendar } from 'react-feather';
import { useReactToPrint } from 'react-to-print';
import { apiPost } from '../../../Utils';
import Breadcrumbs from '../../../CommonElements/Breadcrumbs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../../Context/AuthContext';
import { useConfig } from '../../../Context/ConfigContext';
import DataTable from 'react-data-table-component';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const MaintenanceReport = () => {
    const { hasPermission } = useAuth();
    const { config } = useConfig();
    const componentRef = useRef(null);

    // Dropdown Data
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);

    // Filters State
    const [branchId, setBranchId] = useState('');
    const [vehicleId, setVehicleId] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [filteredVehicles, setFilteredVehicles] = useState<any[]>([]);

    // Data State
    const [reportData, setReportData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // Sorting State
    const [sortBy, setSortBy] = useState('service_date');
    const [sortOrder, setSortOrder] = useState('desc');

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [perPage, setPerPage] = useState(10);

    const printRef = useRef(null);
    const [printData, setPrintData] = useState<any[]>([]);
    const prevSearch = useRef(searchTerm);

    // Columns
    const columns = [
        {
            name: <div>Sl.No</div>,
            cell: (row: any, index: number) => (currentPage - 1) * perPage + index + 1,
            width: '70px',
            center: true,
        },

        {
            name: <div>Vehicle</div>,
            selector: (row: any) => row.registration_number,
            sortable: true,
            cell: (row: any) => (
                <span className={`${row.vehicle_deleted_at ? "text-danger" : "text-primary"}`}>
                    {row.registration_number} {row.vehicle_deleted_at && "(Deleted)"}
                </span>
            ),
            sortField: 'registration_number',
            wrap: true
        },
        {
            name: <div>Branch</div>,
            selector: (row: any) => row.branch_name,
            sortable: true,
            sortField: 'branch_name',
            cell: (row: any) => (
                <span className={row.branch_deleted_at ? "text-danger" : ""}>
                    {row.branch_name} {row.branch_deleted_at && "(Deleted)"}
                </span>
            ),
            wrap: true
        },
        {
            name: <div>Service Date</div>,
            selector: (row: any) => row.service_date,
            sortable: true,
            cell: (row: any) => row.service_date ? new Date(row.service_date).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-',
            sortField: 'service_date',
            wrap: true
        },
        {
            name: <div>Service Type</div>,
            selector: (row: any) => row.service_type,
            cell: (row: any) => row.service_type || (row.parts_vendor_name ? 'Parts Replacement' : '-'),
            sortable: true,
            sortField: 'service_type',
            wrap: true
        },
        {
            name: <div>Garage / Center</div>,
            selector: (row: any) => row.garage_name || row.parts_vendor_name,
            cell: (row: any) => {
                const parts = row.parts_vendor_name;
                const garage = row.garage_name;

                return (
                    <div className="d-flex flex-column text-start">
                        {garage && <small><span className="fw-bold text-secondary">Garage:</span> {garage}</small>}
                        {parts && <small><span className="fw-bold text-secondary">Parts:</span> {parts}</small>}
                        {!garage && !parts && '-'}
                    </div>
                );
            },
            sortable: true,
            sortField: 'garage_name',
            wrap: true
        },
        {
            name: <div>Odometer</div>,
            selector: (row: any) => row.odometer_reading,
            sortable: true,
            cell: (row: any) => row.odometer_reading ? Number(row.odometer_reading).toLocaleString() : '-',
            sortField: 'odometer_reading',
        },
        {
            name: <div>Total Cost ({config.currency})</div>,
            selector: (row: any) => row.total_cost,
            sortable: true,
            cell: (row: any) => <span className="fw-bold text-success">{row.total_cost ? Number(row.total_cost).toLocaleString() : '-'}</span>,
            sortField: 'total_cost',
        },
        {
            name: <div>Next Due</div>,
            selector: (row: any) => row.next_service_date,
            sortable: true,
            cell: (row: any) => row.next_service_date ? new Date(row.next_service_date).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-',
            sortField: 'next_service_date',
            wrap: true
        }
    ];

    // Fetch Dropdown Data
    useEffect(() => {
        apiPost("branches/list").then((res: any) => {
            setBranches(Array.isArray(res) ? res : []);
        }).catch(err => console.error(err));
    }, []);

    // Filter Vehicles when Branch Changes
    useEffect(() => {
        const payload: any = {};
        if (branchId) payload.branch_id = branchId;
        setVehicleId('');
        apiPost("vehicles/list", payload).then((res: any) => {
            setFilteredVehicles(Array.isArray(res) ? res : []);
        }).catch(() => setFilteredVehicles([]));
    }, [branchId]);

    const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Main Data Fetch
    const fetchReport = () => {
        setLoading(true);
        const payload: any = {
            page: currentPage,
            per_page: perPage,
            sort_by: sortBy,
            sort_order: sortOrder,
            search: searchTerm,
            from_date: startDate ? formatDate(startDate) : '',
            to_date: endDate ? formatDate(endDate) : ''
        };
        if (branchId) payload.branch_id = branchId;
        if (vehicleId) payload.vehicle_id = vehicleId;

        apiPost("reports/maintenance", payload).then((res: any) => {
            if (res) {
                setReportData(res.data || []);
                setTotalRecords(res.total || 0);
                setTotalPages(res.last_page || 1);
            }
            setLoading(false);
        }).catch(err => {
            console.error(err);
            setLoading(false);
        });
    };

    // Effect Hooks for Fetching
    // Unified Effect Hook for Fetching
    useEffect(() => {
        const isSearchChanged = prevSearch.current !== searchTerm;
        prevSearch.current = searchTerm;

        if (isSearchChanged && currentPage !== 1) {
            setCurrentPage(1);
            return;
        }

        const delay = isSearchChanged ? 500 : 0;
        const timer = setTimeout(() => {
            fetchReport();
        }, delay);
        return () => clearTimeout(timer);
    }, [branchId, vehicleId, currentPage, perPage, sortBy, sortOrder, searchTerm, startDate, endDate]);


    const handlePageChange = (newPage: number) => setCurrentPage(newPage);
    const handlePerRowsChange = (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };
    const handleSort = (column: any, sortDirection: string) => {
        setSortBy(column.sortField || 'service_date');
        setSortOrder(sortDirection);
    };

    // PDF Download
    const handleDownload = async () => {
        setLoading(true);
        try {
            const payload: any = {
                page: 1,
                per_page: totalRecords > 0 ? totalRecords : 10000,
                sort_by: sortBy,
                sort_order: sortOrder,
                search: searchTerm,
                from_date: startDate ? formatDate(startDate) : '',
                to_date: endDate ? formatDate(endDate) : ''
            };
            if (branchId) payload.branch_id = branchId;
            if (vehicleId) payload.vehicle_id = vehicleId;

            const res: any = await apiPost("reports/maintenance", payload);
            const allData = res.data || [];

            const doc = new jsPDF('l', 'mm', 'a4') as any; // Landscape for more columns
            doc.text("Maintenance Report", 14, 20);
            doc.setFontSize(10);
            doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 26);

            if (startDate && endDate) {
                doc.text(`Period: ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`, 14, 32);
            }

            const tableRows = allData.map((item: any, index: number) => {
                return [
                    index + 1,
                    item.branch_name || '-',
                    item.registration_number || '-',
                    item.service_date ? new Date(item.service_date).toLocaleDateString('en-GB') : '-',
                    item.service_type || (item.parts_vendor_name ? 'Parts Replacement' : '-'),
                    [
                        (item.garage_name ? `Garage: ${item.garage_name}` : ''),
                        (item.parts_vendor_name ? `Parts: ${item.parts_vendor_name}` : '')
                    ].filter(Boolean).join(' / ') || '-',
                    item.odometer_reading ? Number(item.odometer_reading).toLocaleString() : '-',
                    item.total_cost ? Number(item.total_cost).toLocaleString() : '-',
                    item.next_service_date ? new Date(item.next_service_date).toLocaleDateString('en-GB') : '-'
                ];
            });

            autoTable(doc, {
                startY: startDate && endDate ? 40 : 35,
                head: [['Sl.No', 'Branch', 'Vehicle', 'Service Date', 'Type', 'Garage', 'Odometer', `Cost (${config.currency})`, 'Next Due']],
                body: tableRows,
                styles: { fontSize: 9 },
                headStyles: { fillColor: [0, 150, 136] }
            });

            doc.save('Maintenance_Report.pdf');
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Print Handler
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        onBeforeGetContent: async () => {
            setLoading(true);
            try {
                const payload: any = {
                    page: 1,
                    per_page: totalRecords > 0 ? totalRecords : 10000,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    search: searchTerm,
                    from_date: startDate ? formatDate(startDate) : '',
                    to_date: endDate ? formatDate(endDate) : ''
                };
                if (branchId) payload.branch_id = branchId;
                if (vehicleId) payload.vehicle_id = vehicleId;

                const res: any = await apiPost("reports/maintenance", payload);
                setPrintData(res.data || []);
                return new Promise((resolve) => setTimeout(resolve, 500));
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        },
    });

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: { fontSize: '14px', fontWeight: '600' },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className="page-body">
            <Breadcrumbs parent="Reports" title="Maintenance Report" pageTitle="Maintenance Report" />
            <div className="container-fluid">
                <Row className="d-print-none mb-3">
                    <Col md={4}>
                        <Card className="h-80 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #e0f7fa 0%, #ffffff 100%)', borderLeft: '4px solid #009688' }}>
                            <CardBody className="py-3 d-flex align-items-center justify-content-between">
                                <div>
                                    <h6 className="text-muted text-uppercase small fw-bold mb-2">Total Records</h6>
                                    <h4 className="text-dark mb-0 fw-bold">{totalRecords}</h4>
                                </div>
                                <div className="p-3 bg-white rounded-circle shadow-sm">
                                    <FileText size={24} style={{ color: '#009688' }} />
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={4}></Col>
                    <Col md={4} className="bg-transparent border-0 d-flex justify-content-end align-items-center px-0 flex-wrap gap-2">
                        {hasPermission('maintenance-report.view') && (
                            <>
                                <Button color="success" className="px-4 py-2 rounded-pill d-flex align-items-center gap-2 shadow-sm" onClick={handleDownload}>
                                    <Download size={16} /> <span className="fw-bold">Download</span>
                                </Button>
                                <Button color="primary" className="px-4 py-2 rounded-pill d-flex align-items-center gap-2 shadow-sm" onClick={handlePrint}>
                                    <Printer size={16} /> <span className="fw-bold">Print</span>
                                </Button>
                            </>
                        )}
                    </Col>
                </Row>

                {/* Filters */}
                <Card className="border-0 shadow-sm mb-4 bg-light">
                    <CardBody className="p-4">
                        <Row className="g-3">
                            <Col md={3}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <MapPin size={14} style={{ color: '#009688' }} /> Select Branch
                                </Label>
                                <Input type="select" value={branchId} onChange={(e) => setBranchId(e.target.value)} bsSize="sm" className="form-control" style={{ borderRadius: '8px', border: '1px solid #ced4da', padding: '0.5rem' }}>
                                    <option value="">All Branches</option>
                                    {branches.map((b: any) => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
                                </Input>
                            </Col>
                            <Col md={3}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <Truck size={14} style={{ color: '#009688' }} /> Filter Vehicle
                                </Label>
                                <Input type="select" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} bsSize="sm" className="form-control" style={{ borderRadius: '8px', border: '1px solid #ced4da', padding: '0.5rem' }}>
                                    <option value="">All Vehicles</option>
                                    {filteredVehicles.map((v: any) => <option key={v.id} value={v.id}>{v.registration_number}</option>)}
                                </Input>
                            </Col>
                            <Col md={2}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <Calendar size={14} style={{ color: '#009688' }} /> From Date
                                </Label>
                                <div className="rounded overflow-hidden">
                                    <DatePicker
                                        className="form-control"
                                        selected={startDate}
                                        onChange={(date) => { setStartDate(date); setCurrentPage(1); }}
                                        dateFormat="dd/MM/yyyy"
                                        placeholderText="Start Date"
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                    />
                                </div>
                            </Col>
                            <Col md={2}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <Calendar size={14} style={{ color: '#009688' }} /> To Date
                                </Label>
                                <div className="rounded overflow-hidden">
                                    <DatePicker
                                        className="form-control"
                                        selected={endDate}
                                        onChange={(date) => { setEndDate(date); setCurrentPage(1); }}
                                        dateFormat="dd/MM/yyyy"
                                        placeholderText="End Date"
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                    />
                                </div>
                            </Col>
                            <Col md={2}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <Search size={14} style={{ color: '#009688' }} /> Search
                                </Label>
                                <Input type="text" placeholder="Search ..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} bsSize="sm" className="form-control" style={{ borderRadius: '8px', border: '1px solid #ced4da', padding: '0.5rem', height: '35px' }} />
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Table */}
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}>
                        <Spinner color="primary" />
                    </div>
                ) : (
                    <Card className="border-0 shadow-sm">
                        <CardBody className="p-0">
                            <div className="rounded-3 overflow-hidden">
                                <DataTable
                                    columns={columns}
                                    data={reportData}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalRecords}
                                    paginationDefaultPage={currentPage}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    sortServer
                                    onSort={handleSort}
                                    highlightOnHover
                                    responsive
                                    customStyles={customStyles}
                                    noDataComponent={
                                        <div className="py-5 text-center text-muted">
                                            <p className="mb-0">No maintenance records found</p>
                                        </div>
                                    }
                                />
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* Print Component (Hidden) */}
                <div style={{ display: 'none' }}>
                    <div ref={printRef} style={{ padding: '30px', fontFamily: 'Arial, sans-serif' }}>
                        <div style={{ marginBottom: '20px' }}>
                            <h2 style={{ fontSize: '18px', margin: '0 0 5px 0', fontWeight: 'bold' }}>Maintenance Report</h2>
                            <p style={{ fontSize: '10px', color: '#666', margin: 0 }}>Generated: {new Date().toLocaleDateString()}</p>
                            {startDate && endDate && (
                                <p style={{ fontSize: '10px', color: '#666', margin: '5px 0 0 0' }}>Period: {startDate.toLocaleDateString()} to {endDate.toLocaleDateString()}</p>
                            )}
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px', tableLayout: 'auto' }}>
                            <thead style={{ display: 'table-header-group' }}>
                                <tr style={{ backgroundColor: '#009688', color: '#fff' }}>
                                    <th style={{ padding: '6px', textAlign: 'center', fontWeight: 'bold', border: '1px solid #ddd' }}>Sl.No</th>
                                    <th style={{ padding: '6px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #ddd' }}>Branch</th>
                                    <th style={{ padding: '6px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #ddd' }}>Vehicle</th>
                                    <th style={{ padding: '6px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #ddd' }}>Service Date</th>
                                    <th style={{ padding: '6px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #ddd' }}>Type</th>
                                    <th style={{ padding: '6px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #ddd' }}>Garage/Center</th>
                                    <th style={{ padding: '6px', textAlign: 'right', fontWeight: 'bold', border: '1px solid #ddd' }}>Odometer</th>
                                    <th style={{ padding: '6px', textAlign: 'right', fontWeight: 'bold', border: '1px solid #ddd' }}>Cost ({config.currency})</th>
                                    <th style={{ padding: '6px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #ddd' }}>Next Due</th>
                                </tr>
                            </thead>
                            <tbody>
                                {printData.map((item, index) => (
                                    <tr key={index} style={{ pageBreakInside: 'avoid' }}>
                                        <td style={{ padding: '6px', textAlign: 'center', border: '1px solid #ddd' }}>{index + 1}</td>
                                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{item.branch_name || '-'}</td>
                                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{item.registration_number || '-'}</td>
                                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{item.service_date ? new Date(item.service_date).toLocaleDateString('en-GB') : '-'}</td>
                                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{item.service_type || (item.parts_vendor_name ? 'Parts Replacement' : '-')}</td>
                                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>
                                            {item.garage_name && <div><span style={{ fontWeight: 'bold', color: '#6c757d' }}>Garage:</span> {item.garage_name}</div>}
                                            {item.parts_vendor_name && <div><span style={{ fontWeight: 'bold', color: '#6c757d' }}>Parts:</span> {item.parts_vendor_name}</div>}
                                            {!item.garage_name && !item.parts_vendor_name && '-'}
                                        </td>
                                        <td style={{ padding: '6px', textAlign: 'right', border: '1px solid #ddd', fontFamily: 'monospace' }}>
                                            {item.odometer_reading ? Number(item.odometer_reading).toLocaleString() : '-'}
                                        </td>
                                        <td style={{ padding: '6px', textAlign: 'right', border: '1px solid #ddd', fontFamily: 'monospace' }}>
                                            {item.total_cost ? Number(item.total_cost).toLocaleString() : '-'}
                                        </td>
                                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{item.next_service_date ? new Date(item.next_service_date).toLocaleDateString('en-GB') : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={{ marginTop: '20px', paddingTop: '10px', borderTop: '1px solid #eee', textAlign: 'center', color: '#999', fontSize: '10px' }}>
                            Fleet Management System © {new Date().getFullYear()}
                        </div>
                    </div>
                </div>
            </div>
            <style>{`@media print { .d-print-none { display: none !important; } .d-print-block { display: block !important; } .page-body { padding: 0; margin: 0; } .card { border: none !important; box-shadow: none !important; } }`}</style>
        </div>
    );
};

export default MaintenanceReport;
